import { Box, Divider, Fade, Typography, useTheme } from "@mui/material";
import React from "react";
import { getOrDefault } from "../../../Helpers/Utils";
import { SectionsDividerProps } from "./types";

const SectionsDivider: React.FC<SectionsDividerProps> = ({ spec, type, index }) => {
    const theme = useTheme();

    // Enhanced properties with more options
    const baseBackground = getOrDefault(spec.baseBackground, "transparent");
    const dividerBackground = getOrDefault(spec.dividerBackground, "#3f3e45");
    const thickness = getOrDefault(spec.thickness, "1px");
    const width = getOrDefault(spec.width, "100%");
    const marginTop = getOrDefault(spec.marginTop, "0px");
    const marginBottom = getOrDefault(spec.marginBottom, "0px");
    const paddingX = getOrDefault(spec.paddingX, "0");
    const style = getOrDefault(spec.style, "solid"); // solid, dashed, dotted, gradient, double
    const alignment = getOrDefault(spec.alignment, "center"); // left, center, right
    const borderRadius = getOrDefault(spec.borderRadius, "0px");
    const shadow = getOrDefault(spec.shadow, false);
    const glowEffect = getOrDefault(spec.glowEffect, false);
    const animated = getOrDefault(spec.animated, false);
    const label = getOrDefault(spec.label, null);
    const labelColor = getOrDefault(spec.labelColor, theme.palette.text.secondary);
    const labelBackground = getOrDefault(spec.labelBackground, baseBackground);
    const gradientStart = getOrDefault(spec.gradientStart, dividerBackground);
    const gradientEnd = getOrDefault(spec.gradientEnd, dividerBackground);
    const opacity = getOrDefault(spec.opacity, 1);
    const hover = getOrDefault(spec.hover, false);
    const hoverColor = getOrDefault(spec.hoverColor, theme.palette.primary.main);

    // Dynamic styles based on spec
    const getDividerBackground = (): string => {
        switch (style) {
            case "gradient":
                return `linear-gradient(90deg, ${gradientStart} 0%, ${gradientEnd} 100%)`;
            case "double":
                return `repeating-linear-gradient(
                    90deg,
                    ${dividerBackground} 0px,
                    ${dividerBackground} ${thickness},
                    transparent ${thickness},
                    transparent calc(${thickness} * 2)
                )`;
            default:
                return dividerBackground;
        }
    };

    const getDividerStyle = () => {
        const baseStyle = {
            width,
            height: style === "double" ? `calc(${thickness} * 3)` : thickness,
            borderRadius,
            opacity,
            transition: 'all 0.3s ease',
            transform: animated ? 'scaleX(1)' : 'scaleX(1)',
            transformOrigin: alignment === 'left' ? 'left' : alignment === 'right' ? 'right' : 'center',
        };

        if (style === "dashed" || style === "dotted") {
            return {
                ...baseStyle,
                border: 'none',
                borderTop: `${thickness} ${style} ${dividerBackground}`,
                height: '0px',
                background: 'none'
            };
        }

        return {
            ...baseStyle,
            background: getDividerBackground(),
            boxShadow: shadow ? `0 2px 8px rgba(0,0,0,0.1)` : 
                      glowEffect ? `0 0 10px ${dividerBackground}` : 'none',
            '&:hover': hover ? {
                background: hoverColor,
                transform: 'scaleY(1.5)',
                boxShadow: `0 0 15px ${hoverColor}`
            } : {}
        };
    };

    const getContainerAlignment = (): string => {
        switch (alignment) {
            case "left":
                return "flex-start";
            case "right":
                return "flex-end";
            default:
                return "center";
        }
    };

    return (
        <Box 
            id={spec.id}
            sx={{
                background: baseBackground,
                paddingTop: marginTop,
                paddingBottom: marginBottom,
                paddingX,
                display: 'flex',
                justifyContent: getContainerAlignment(),
                alignItems: 'center',
                position: 'relative'
            }}
            key={`${type}-${index}-box`}
        >
            {label ? (
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    width: '100%',
                    position: 'relative'
                }}>
                    <Divider
                        sx={{
                            ...getDividerStyle(),
                            flex: 1,
                            marginRight: 2
                        }}
                    />
                    <Fade in={true} timeout={800}>
                        <Box>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: labelColor,
                                    background: labelBackground,
                                    padding: '8px 16px',
                                    borderRadius: '20px',
                                    fontWeight: 500,
                                    whiteSpace: 'nowrap',
                                    border: `1px solid ${dividerBackground}20`,
                                    boxShadow: shadow ? '0 2px 8px rgba(0,0,0,0.1)' : 'none'
                                }}
                            >
                                {label}
                            </Typography>
                        </Box>
                    </Fade>
                    <Divider
                        sx={{
                            ...getDividerStyle(),
                            flex: 1,
                            marginLeft: 2
                        }}
                    />
                </Box>
            ) : (
                <Fade in={true} timeout={600}>
                    <Divider
                        key={`${type}-${index}-divider`}
                        sx={getDividerStyle()}
                    />
                </Fade>
            )}
        </Box>
    )
}

export default SectionsDivider;
